import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Button, PageHeader, Badge, Input } from '../components/ui';
import { useSelectedBusinessId } from '../hooks/useSelectedBusiness';
import {
  Star,
  RefreshCw,
  Loader2,
  AlertCircle,
  X,
  MessageSquare,
  CheckCircle,
  EyeOff,
  Sparkles,
  Copy,
  Check,
  Plus,
  ClipboardPaste,
  FileText,
  ClipboardList,
} from 'lucide-react';
import { reviewsApi, reviewTemplateApi, yandexApi, type Business, type Review, type ReviewStatus, type ReviewSource, type ReviewAnalytics, type ReviewTemplate } from '../lib/api';
import { getBusinessList } from '../lib/businessListCache';

const STATUS_TABS: { value: ReviewStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'Все' },
  { value: 'NEW', label: 'Новые' },
  { value: 'RESPONDED', label: 'Отвечено' },
  { value: 'IGNORED', label: 'Игнорировано' },
];

const SOURCE_LABELS: Record<ReviewSource, string> = {
  YANDEX: 'Яндекс',
  TWOGIS: '2ГИС',
  GOOGLE: 'Google',
  MANUAL: 'Вручную',
  IMPORT: 'Импорт',
};

// Per-source colors stay DISTINCT so sources remain visually distinguishable.
// Badge has only 4 tones for 5 sources, so explicit non-accent color classes are
// passed to Badge via className. GOOGLE deliberately uses blue (a categorical
// color), NOT the indigo `primary` accent, so it never collides with accent UI.
const SOURCE_COLORS: Record<ReviewSource, string> = {
  YANDEX: 'bg-red-100 text-red-700',
  TWOGIS: 'bg-green-100 text-green-700',
  GOOGLE: 'bg-blue-100 text-blue-700',
  MANUAL: 'bg-gray-100 text-gray-600',
  IMPORT: 'bg-purple-100 text-purple-700',
};

function SourceBadge({ source, label }: { source: ReviewSource; label?: string }) {
  return (
    <Badge className={SOURCE_COLORS[source] || 'bg-gray-100 text-gray-600'}>
      {label ?? SOURCE_LABELS[source] ?? source}
    </Badge>
  );
}

// Review STATUS → Badge tone mapping.
const STATUS_BADGE: Record<ReviewStatus, { tone: 'success' | 'warning' | 'neutral'; label: string }> = {
  NEW: { tone: 'warning', label: 'Новый' },
  RESPONDED: { tone: 'success', label: 'Отвечено' },
  IGNORED: { tone: 'neutral', label: 'Игнорировано' },
};

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((s) => (
        <Star
          key={s}
          className={`w-4 h-4 ${s <= rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-200 fill-gray-200'}`}
        />
      ))}
    </div>
  );
}

const TEXT_PREVIEW_LENGTH = 250;

function ReviewCard({ review, onReload, templates }: {
  review: Review;
  onReload: () => void;
  templates: ReviewTemplate[];
}) {
  const [isGenerating, setIsGenerating] = useState(false);
  const [responses, setResponses] = useState<string[]>(review.aiResponses || []);
  const [selectedResponse, setSelectedResponse] = useState('');
  const [editableResponse, setEditableResponse] = useState('');
  const [showEditor, setShowEditor] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const templatesRef = useRef<HTMLDivElement>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ text: string; type: 'success' | 'warning' } | null>(null);
  const [copiedIdx, setCopiedIdx] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const cleanedText = review.text.replace(/\.{2,}\s*Ещё\s*$/i, '').replace(/\s*Ещё\s*$/i, '').trim();
  const isLong = cleanedText.length > TEXT_PREVIEW_LENGTH;

  // Templates ordered so that those matching the review's sentiment come first.
  // Stable for the rest; if sentiment is undefined, original order is preserved.
  const orderedTemplates = review.sentiment
    ? templates
        .map((t, idx) => ({ t, idx }))
        .sort((a, b) => {
          const am = a.t.category === review.sentiment ? 0 : 1;
          const bm = b.t.category === review.sentiment ? 0 : 1;
          return am - bm || a.idx - b.idx;
        })
        .map((x) => x.t)
    : templates;

  // Close templates popover on outside-click / Esc.
  useEffect(() => {
    if (!showTemplates) return;
    const onClick = (e: MouseEvent) => {
      if (templatesRef.current && !templatesRef.current.contains(e.target as Node)) {
        setShowTemplates(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setShowTemplates(false);
    };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [showTemplates]);

  const handleSelectTemplate = (template: ReviewTemplate) => {
    // Reveal the editor the same way the AI-variant flow does, then FULL-REPLACE
    // the current draft with the template content. No append, no submit, no API.
    setShowEditor(true);
    setEditableResponse(template.content);
    setShowTemplates(false);
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    try {
      const res = await reviewsApi.generateResponse(review.id);
      setResponses(res.data.data.responses);
      setSelectedResponse('');
      setEditableResponse('');
      setShowEditor(false);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (text: string, idx: number) => {
    navigator.clipboard.writeText(text);
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  };

  const handleSave = async () => {
    const responseToSave = editableResponse.trim();
    if (!responseToSave) return;
    setIsSaving(true);
    try {
      const res = await reviewsApi.saveResponse(review.id, responseToSave);
      const delivery = res.data.data.delivery;
      setSaveMessage({
        text: delivery.message,
        type: delivery.published ? 'success' : 'warning',
      });
      setShowEditor(false);
      onReload();
    } finally {
      setIsSaving(false);
    }
  };

  const openEditor = (baseText?: string | null) => {
    const initialText = (baseText || '').trim();
    setSelectedResponse(initialText);
    setEditableResponse(initialText);
    setShowEditor(true);
  };

  const handleIgnore = async () => {
    await reviewsApi.ignore(review.id);
    onReload();
  };

  const handleDelete = async () => {
    if (!confirm('Удалить этот отзыв?')) return;
    await reviewsApi.delete(review.id);
    onReload();
  };

  const status = STATUS_BADGE[review.status];
  const statusBadge = <Badge tone={status.tone}>{status.label}</Badge>;

  const cardBorderColor = {
    NEW: 'border-l-4 border-l-primary-300',
    RESPONDED: 'border-l-4 border-l-green-400',
    IGNORED: 'border-l-4 border-l-gray-300 opacity-60',
  }[review.status];

  return (
    <Card className={`space-y-3 ${cardBorderColor}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <span className="font-medium text-gray-900">{review.authorName}</span>
            {statusBadge}
            {review.source && <SourceBadge source={review.source} />}
          </div>
          <StarRating rating={review.rating} />
          <span className="text-xs text-gray-400 mt-1 block">
            {new Date(review.publishedAt).toLocaleDateString('ru-RU')}
          </span>
        </div>
        <button
          onClick={handleDelete}
          className="p-1 text-gray-300 hover:text-red-500 transition-colors flex-shrink-0"
          title="Удалить отзыв"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="text-sm text-gray-700 leading-relaxed">
        <p>
          {isExpanded || !isLong
            ? cleanedText
            : cleanedText.slice(0, TEXT_PREVIEW_LENGTH) + '...'}
        </p>
        {isLong && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="text-primary-600 hover:text-primary-700 text-xs font-medium mt-1"
          >
            {isExpanded ? 'Скрыть' : 'Читать полностью'}
          </button>
        )}
      </div>

      {review.response && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
          <p className="text-xs font-medium text-green-700 mb-1">Ваш ответ:</p>
          <p className="text-sm text-green-800">{review.response}</p>
          {review.status !== 'IGNORED' && (
            <div className="mt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => openEditor(review.response)}
              >
                Редактировать / отправить снова
              </Button>
            </div>
          )}
        </div>
      )}

      {saveMessage && (
        <div className={`rounded-lg border p-3 text-sm ${
          saveMessage.type === 'success'
            ? 'bg-green-50 border-green-200 text-green-800'
            : 'bg-amber-50 border-amber-200 text-amber-800'
        }`}>
          {saveMessage.text}
        </div>
      )}

      {(responses.length > 0 || showEditor) && review.status !== 'IGNORED' && (
        <div className="space-y-2">
          {responses.length > 0 && <p className="text-xs font-medium text-gray-500">Варианты ответа:</p>}
          {responses.map((r, idx) => (
            <div
              key={idx}
              onClick={() => {
                setSelectedResponse(r);
                setEditableResponse(r);
                setShowEditor(true);
              }}
              className={`p-3 rounded-lg border cursor-pointer text-sm transition-colors ${
                selectedResponse === r
                  ? 'border-primary-400 bg-primary-50'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <p className="text-gray-700 flex-1">{r}</p>
                <button
                  onClick={(e) => { e.stopPropagation(); handleCopy(r, idx); }}
                  className="flex-shrink-0 p-1 text-gray-400 hover:text-gray-600"
                >
                  {copiedIdx === idx ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>
          ))}
          {showEditor && (
            <div className="space-y-2">
              <label className="text-xs font-medium text-gray-500">Редактирование перед отправкой:</label>
              <textarea
                value={editableResponse}
                onChange={(e) => setEditableResponse(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700 focus:border-primary-400 focus:outline-none"
                placeholder="При необходимости поправьте текст ответа..."
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSave} loading={isSaving} icon={<CheckCircle className="w-4 h-4" />}>
                  {review.status === 'RESPONDED' ? 'Сохранить и отправить снова' : 'Сохранить ответ'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setEditableResponse(selectedResponse || review.response || '')}
                >
                  Сбросить
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {review.status !== 'IGNORED' && (
        <div className="flex gap-2 pt-1 flex-wrap">
          <Button
            size="sm"
            variant="outline"
            icon={isGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            onClick={handleGenerate}
            loading={isGenerating}
          >
            {responses.length > 0 ? 'Обновить варианты' : 'Сгенерировать ответ'}
          </Button>
          <div className="relative" ref={templatesRef}>
            <Button
              size="sm"
              variant="outline"
              icon={<ClipboardList className="w-4 h-4" />}
              onClick={() => setShowTemplates((v) => !v)}
            >
              Шаблоны
            </Button>
            {showTemplates && (
              <div className="absolute z-20 mt-1 w-72 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg p-1">
                {orderedTemplates.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-gray-500">
                    Нет шаблонов — создайте в Настройках
                  </div>
                ) : (
                  orderedTemplates.map((t) => (
                    <button
                      key={t.id}
                      onClick={() => handleSelectTemplate(t)}
                      className="w-full text-left px-3 py-2 rounded-md hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium text-gray-800 truncate">{t.name}</span>
                        {t.category === review.sentiment && (
                          <Badge tone="success">подходит</Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 truncate mt-0.5">{t.content}</p>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          {review.status === 'RESPONDED' ? (
            <Button
              size="sm"
              variant="outline"
              icon={<CheckCircle className="w-4 h-4" />}
              onClick={() => openEditor(review.response)}
            >
              Переотправить ответ
            </Button>
          ) : (
            <>
              <Button
                size="sm"
                variant="outline"
                icon={<CheckCircle className="w-4 h-4" />}
                onClick={() => reviewsApi.markResponded(review.id).then(onReload)}
                className="text-green-600 hover:text-green-700 hover:border-green-300"
                title="Отметить как отвеченный (без сохранения текста)"
              >
                Уже отвечено
              </Button>
              <Button
                size="sm"
                variant="outline"
                icon={<EyeOff className="w-4 h-4" />}
                onClick={handleIgnore}
                className="text-gray-500"
              >
                Игнорировать
              </Button>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

// ==================== Analytics Panel ====================

const RATING_COLORS = ['', 'bg-red-500', 'bg-orange-400', 'bg-yellow-400', 'bg-lime-400', 'bg-green-500'];

function AnalyticsPanel({ analytics }: { analytics: ReviewAnalytics }) {
  const maxRating = Math.max(...Object.values(analytics.ratingDistribution), 1);

  return (
    <Card>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-900">{analytics.totalReviews}</div>
          <div className="text-xs text-gray-500">Всего отзывов</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-yellow-600">
            <span className="inline-flex items-center gap-1">
              <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
              {analytics.averageRating}
            </span>
          </div>
          <div className="text-xs text-gray-500">Средний рейтинг</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-green-600">{analytics.responseRate}%</div>
          <div className="text-xs text-gray-500">Ответов</div>
        </div>
        <div className="text-center">
          <div className="text-2xl font-bold text-gray-700">
            {analytics.ratingTrend.length >= 2
              ? (() => {
                  const prev = analytics.ratingTrend[analytics.ratingTrend.length - 2].avg;
                  const curr = analytics.ratingTrend[analytics.ratingTrend.length - 1].avg;
                  const delta = +(curr - prev).toFixed(1);
                  return delta >= 0 ? `+${delta}` : `${delta}`;
                })()
              : '—'}
          </div>
          <div className="text-xs text-gray-500">Динамика рейтинга</div>
        </div>
      </div>

      {/* Rating distribution */}
      <div className="mb-4">
        <p className="text-xs font-medium text-gray-500 mb-2">Распределение оценок</p>
        <div className="space-y-1">
          {[5, 4, 3, 2, 1].map((r) => {
            const count = analytics.ratingDistribution[r] || 0;
            const pct = maxRating > 0 ? (count / maxRating) * 100 : 0;
            return (
              <div key={r} className="flex items-center gap-2 text-xs">
                <span className="w-4 text-gray-500 text-right">{r}</span>
                <Star className="w-3 h-3 text-yellow-400 fill-yellow-400" />
                <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden">
                  <div className={`h-full rounded ${RATING_COLORS[r]}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="w-8 text-gray-400 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Source + Topics row */}
      <div className="flex gap-6 flex-wrap">
        {analytics.sourceBreakdown.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Источники</p>
            <div className="flex gap-1 flex-wrap">
              {analytics.sourceBreakdown.map((s) => (
                <SourceBadge
                  key={s.source}
                  source={s.source as ReviewSource}
                  label={`${SOURCE_LABELS[s.source as ReviewSource] || s.source} (${s.count})`}
                />
              ))}
            </div>
          </div>
        )}
        {analytics.topTopics.length > 0 && (
          <div>
            <p className="text-xs font-medium text-gray-500 mb-1">Темы</p>
            <div className="flex gap-1 flex-wrap">
              {analytics.topTopics.slice(0, 5).map((t) => (
                <Badge key={t.topic} tone="neutral">
                  {t.topic} ({t.count})
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </Card>
  );
}

// ==================== Main Component ====================

export function Reviews() {
  const selectedBusinessId = useSelectedBusinessId() ?? '';
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [templates, setTemplates] = useState<ReviewTemplate[]>([]);
  const [analytics, setAnalytics] = useState<ReviewAnalytics | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({ NEW: 0, RESPONDED: 0, IGNORED: 0 });
  const [searchParams] = useSearchParams();
  const highlightedReviewId = searchParams.get('highlight');
  const [activeTab, setActiveTab] = useState<ReviewStatus | 'ALL'>(() =>
    highlightedReviewId ? 'NEW' : 'ALL'
  );
  const [activeSource, setActiveSource] = useState<ReviewSource | 'ALL'>('ALL');
  const [isLoading, setIsLoading] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [showAnalytics, setShowAnalytics] = useState(true);
  const [yandexConnected, setYandexConnected] = useState<boolean | null>(null);
  const [yandexPublishingReady, setYandexPublishingReady] = useState<boolean | null>(null);
  const [yandexPublishingHint, setYandexPublishingHint] = useState<string | null>(null);

  // Modal states
  const [showAddForm, setShowAddForm] = useState(false);
  const [showImportForm, setShowImportForm] = useState(false);
  const [addForm, setAddForm] = useState({ authorName: '', rating: 5, text: '' });
  const [importText, setImportText] = useState('');
  const [isImporting, setIsImporting] = useState(false);

  const highlightedCardRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    getBusinessList().then(setBusinesses).catch(() => {});
  }, []);

  useEffect(() => {
    let active = true;
    reviewTemplateApi.getAll()
      .then((r) => {
        if (active) setTemplates(r.data.data);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (selectedBusinessId) {
      loadReviews();
      loadAnalytics();
      yandexApi.getStatus(selectedBusinessId)
        .then((response) => {
          const connected = response.data.data.connected;
          const publishingReady = response.data.data.reviewPublishingReady ?? connected;
          setYandexConnected(connected);
          setYandexPublishingReady(publishingReady);
          setYandexPublishingHint(response.data.data.reviewPublishingHint || null);
        })
        .catch(() => {
          setYandexConnected(null);
          setYandexPublishingReady(null);
          setYandexPublishingHint(null);
        });
    }
  }, [selectedBusinessId, activeTab, activeSource]);

  useEffect(() => {
    if (!highlightedReviewId || reviews.length === 0) return;
    const timer = setTimeout(() => {
      highlightedCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
    return () => clearTimeout(timer);
  }, [highlightedReviewId, reviews]);

  const loadAnalytics = async () => {
    try {
      const response = await reviewsApi.getAnalytics(selectedBusinessId);
      setAnalytics(response.data.data);
    } catch {
      // Analytics is non-critical, don't show error
    }
  };

  const loadReviews = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const status = activeTab === 'ALL' ? undefined : activeTab;
      const source = activeSource === 'ALL' ? undefined : activeSource;
      const response = await reviewsApi.getByBusiness(selectedBusinessId, status, source);
      setReviews(response.data.data.reviews);
      setStatusCounts(response.data.data.statusCounts);
    } catch {
      setError('Не удалось загрузить отзывы');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async () => {
    try {
      setIsSyncing(true);
      setSuccessMessage(null);
      const response = await reviewsApi.sync(selectedBusinessId);
      setSuccessMessage(response.data.data.message);
      await loadReviews();
    } catch {
      setError('Ошибка синхронизации');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleAddReview = async () => {
    if (!addForm.authorName || !addForm.text) return;
    try {
      await reviewsApi.create({
        businessId: selectedBusinessId,
        authorName: addForm.authorName,
        rating: addForm.rating,
        text: addForm.text,
      });
      setShowAddForm(false);
      setAddForm({ authorName: '', rating: 5, text: '' });
      setSuccessMessage('Отзыв добавлен');
      await loadReviews();
    } catch {
      setError('Не удалось добавить отзыв');
    }
  };

  const handleImportText = async () => {
    if (!importText.trim()) return;
    try {
      setIsImporting(true);
      setError(null);
      const response = await reviewsApi.importText(selectedBusinessId, importText);
      setShowImportForm(false);
      setImportText('');
      setSuccessMessage(response.data.data.message);
      await loadReviews();
    } catch {
      setError('Не удалось импортировать отзывы');
    } finally {
      setIsImporting(false);
    }
  };

  const selectedBusiness = businesses.find((b) => b.id === selectedBusinessId);
  const canConnectYandex = !!selectedBusinessId && !!selectedBusiness?.yandexMapsUrl;

  return (
    <div>
      <PageHeader title="Отзывы" subtitle="Управление отзывами и AI-генерация ответов" />

      <div className="p-6 space-y-6">
        {error && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{error}</span>
            <button onClick={() => setError(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {successMessage && (
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
            <CheckCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1">{successMessage}</span>
            <button onClick={() => setSuccessMessage(null)} className="ml-auto"><X className="w-4 h-4" /></button>
          </div>
        )}

        {canConnectYandex && (yandexConnected === false || yandexPublishingReady === false) && (
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-3 text-amber-800">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="flex-1 text-sm">
              {yandexConnected === false
                ? 'Яндекс не подключен. Ответы на отзывы сохраняются как черновики и не публикуются в Яндекс.Бизнес.'
                : (yandexPublishingHint || 'Автоотправка ответов в Яндекс пока отключена на сервере.')}
            </span>
            {yandexConnected === false && (
              <Button
                size="sm"
                onClick={() => {
                  window.location.href = yandexApi.getOAuthStartUrl(selectedBusinessId);
                }}
              >
                Подключить Яндекс
              </Button>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex gap-2 ml-auto flex-wrap">
            <Button
              size="sm"
              icon={<ClipboardPaste className="w-4 h-4" />}
              onClick={() => setShowImportForm(true)}
            >
              Вставить из текста
            </Button>
            <Button
              size="sm"
              variant="outline"
              icon={<Plus className="w-4 h-4" />}
              onClick={() => setShowAddForm(true)}
            >
              Добавить вручную
            </Button>
            {(selectedBusiness?.yandexMapsUrl || selectedBusiness?.twogisUrl) && (
              <Button
                variant="outline"
                size="sm"
                icon={isSyncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                onClick={handleSync}
                loading={isSyncing}
                title="Синхронизация отзывов с Яндекс.Карт и 2ГИС"
              >
                Синхронизация
              </Button>
            )}
          </div>
        </div>

        {/* Status tabs */}
        <div className="flex gap-1 border-b border-gray-200">
          {STATUS_TABS.map((tab) => {
            const count = tab.value === 'ALL'
              ? Object.values(statusCounts).reduce((a: number, b: number) => a + b, 0)
              : (statusCounts[tab.value] || 0);
            return (
              <button
                key={tab.value}
                onClick={() => setActiveTab(tab.value)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === tab.value
                    ? 'border-primary-600 text-primary-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
                <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-xs ${
                  activeTab === tab.value ? 'bg-primary-100' : 'bg-gray-100'
                }`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Source filter */}
        <div className="flex gap-2 flex-wrap">
          {(['ALL', 'YANDEX', 'TWOGIS', 'GOOGLE', 'MANUAL', 'IMPORT'] as const).map((src) => (
            <button
              key={src}
              onClick={() => setActiveSource(src)}
              className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                activeSource === src
                  ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {src === 'ALL' ? 'Все источники' : SOURCE_LABELS[src]}
            </button>
          ))}
        </div>

        {/* Analytics panel */}
        {analytics && analytics.totalReviews > 0 && (
          <div>
            <button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className="text-xs text-gray-500 hover:text-gray-700 mb-2 flex items-center gap-1"
            >
              {showAnalytics ? 'Скрыть аналитику' : 'Показать аналитику'}
            </button>
            {showAnalytics && <AnalyticsPanel analytics={analytics} />}
          </div>
        )}

        {/* Import text modal */}
        {showImportForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-lg font-semibold">Импорт отзывов из текста</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Скопируйте отзывы из Яндекс.Бизнеса, 2ГИС или любого источника и вставьте сюда.
                    AI автоматически извлечёт имя автора, рейтинг и текст каждого отзыва.
                  </p>
                </div>
                <button onClick={() => setShowImportForm(false)} className="flex-shrink-0"><X className="w-5 h-5" /></button>
              </div>

              <div className="space-y-4">
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                  <p className="text-xs font-medium text-gray-600 mb-2">Пример формата (подойдёт любой):</p>
                  <pre className="text-xs text-gray-500 whitespace-pre-wrap font-mono">
{`Иван Иванов - 5 звёзд
Отличное место! Быстро обслужили, вкусная еда.

Мария П. - 3 звезды
Долго ждали заказ, но в целом нормально.

Аноним, оценка 1
Ужасный сервис, больше не приду.`}
                  </pre>
                </div>

                <textarea
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 font-mono text-sm"
                  rows={10}
                  value={importText}
                  onChange={(e) => setImportText(e.target.value)}
                  placeholder="Вставьте отзывы сюда..."
                />

                <div className="flex gap-3">
                  <Button
                    onClick={handleImportText}
                    loading={isImporting}
                    icon={isImporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
                  >
                    {isImporting ? 'AI парсит отзывы...' : 'Импортировать'}
                  </Button>
                  <Button variant="outline" onClick={() => { setShowImportForm(false); setImportText(''); }}>
                    Отмена
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Add review modal */}
        {showAddForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Добавить отзыв</h2>
                <button onClick={() => setShowAddForm(false)}><X className="w-5 h-5" /></button>
              </div>
              <div className="space-y-4">
                <Input
                  label="Имя автора"
                  value={addForm.authorName}
                  onChange={(e) => setAddForm((p) => ({ ...p, authorName: e.target.value }))}
                  placeholder="Иван Иванов"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Рейтинг</label>
                  <div className="flex gap-1">
                    {[1,2,3,4,5].map((s) => (
                      <button key={s} onClick={() => setAddForm((p) => ({ ...p, rating: s }))}>
                        <Star className={`w-6 h-6 ${s <= addForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Текст отзыва</label>
                  <textarea
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500"
                    rows={4}
                    value={addForm.text}
                    onChange={(e) => setAddForm((p) => ({ ...p, text: e.target.value }))}
                    placeholder="Текст отзыва..."
                  />
                </div>
                <div className="flex gap-3">
                  <Button onClick={handleAddReview}>Добавить</Button>
                  <Button variant="outline" onClick={() => setShowAddForm(false)}>Отмена</Button>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Reviews list */}
        {isLoading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            <MessageSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
            <p className="font-medium">Отзывов пока нет</p>
            <p className="text-sm mt-1 mb-4">Добавьте отзывы одним из способов:</p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button
                size="sm"
                icon={<ClipboardPaste className="w-4 h-4" />}
                onClick={() => setShowImportForm(true)}
              >
                Вставить из текста
              </Button>
              <Button
                size="sm"
                variant="outline"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setShowAddForm(true)}
              >
                Добавить вручную
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {reviews.map((review) => (
              <div
                key={review.id}
                ref={review.id === highlightedReviewId ? highlightedCardRef : undefined}
                className={review.id === highlightedReviewId ? 'ring-2 ring-primary-400 rounded-xl' : undefined}
              >
                <ReviewCard review={review} onReload={loadReviews} templates={templates} />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
